import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { useCallback, useMemo } from 'react';

import ReviewDialog from 'components/Dialogs/ScheduledTransactionAddDialog/ReviewDialog';
import ScheduledTransactionEditDialog from 'components/Dialogs/ScheduledTransactionEditDialog';
import DrawerDialogFrame from 'components/Drawers/DrawerDialogFrame';
import StandardDrawer from 'components/Drawers/StandardDrawer';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import LoadingView from 'components/LoadingView';
import QIllustration from 'components/QIllustration';

import useDeferredDrawerClose from 'components/Drawers/useDeferredDrawerClose';

import RecurringOptions from '../RecurringOptions';

type ScheduledTransactionEditDialogProps = React.ComponentProps<typeof ScheduledTransactionEditDialog>;

type EditorDrawerProps = Omit<
  ScheduledTransactionEditDialogProps,
  'onBack' | 'onClose' | 'open' | 'rootModalDrawer'
> & {
  onBack?: ScheduledTransactionEditDialogProps['onBack'];
  onClose: ScheduledTransactionEditDialogProps['onClose'];
  onExited?: () => void;
  open: boolean;
  rootModalDrawer: RootModalDrawerOptions;
};

type OptionsDrawerProps = React.ComponentProps<typeof RecurringOptions> & {
  onClose: () => void;
  open: boolean;
  rootModalDrawer: RootModalDrawerOptions;
  title: string;
};

type OptionsDrawerLayerProps = React.ComponentProps<typeof RecurringOptions> & {
  onClose: () => void;
  rootModalDrawer?: RootModalDrawerOptions;
  showConfirmation: boolean;
  showEditRecurring: boolean;
  showRecurringOptions: boolean;
  showRecurringSelect: boolean;
  title: string;
  waiting: boolean;
};

type ReviewDrawerProps = {
  edit: (initialTab?: number) => void;
  model: React.ComponentProps<typeof ReviewDialog>['model'];
  onClose: () => void;
  open: boolean;
  rootModalDrawer: RootModalDrawerOptions;
};

type ProgressDrawerProps = {
  loadingString: string;
  onClose: () => void;
  open: boolean;
  rootModalDrawer: RootModalDrawerOptions;
  successString: string | null;
};

const loadingContentStyle = { height: 'auto', margin: '64px 48px' };

function OptionsDrawer({
  onClose,
  open,
  rootModalDrawer,
  title,
  ...recurringOptionsProps
}: OptionsDrawerProps): React.JSX.Element {
  const { animateThen, drawerOpen, handleExited } = useDeferredDrawerClose({ open });
  const handleClose = useCallback(() => animateThen(onClose), [animateThen, onClose]);

  return (
    <StandardDrawer
      closeTransition={rootModalDrawer.closeTransition}
      variant='overlay'
      onClose={handleClose}
      onExited={handleExited}
      open={drawerOpen}
      showScrim={false}
      width={rootModalDrawer.width ?? 'Medium'}
    >
      <DrawerDialogFrame title={title} onBack={handleClose} onClose={handleClose} showBackButton>
        <RecurringOptions {...recurringOptionsProps} />
      </DrawerDialogFrame>
    </StandardDrawer>
  );
}

export function OptionsDrawerLayer({
  onClose,
  rootModalDrawer,
  showConfirmation,
  showEditRecurring,
  showRecurringOptions,
  showRecurringSelect,
  title,
  waiting,
  ...recurringOptionsProps
}: OptionsDrawerLayerProps): React.JSX.Element | null {
  if (!rootModalDrawer || !showRecurringOptions) {
    return null;
  }

  return (
    <OptionsDrawer
      {...recurringOptionsProps}
      onClose={onClose}
      open={(showEditRecurring || showRecurringSelect || !waiting) && !showConfirmation}
      rootModalDrawer={rootModalDrawer}
      title={title}
    />
  );
}

export function EditorDrawer({
  onBack,
  onClose,
  onExited,
  open,
  rootModalDrawer,
  ...dialogProps
}: EditorDrawerProps): React.JSX.Element | null {
  const { animateThen, drawerOpen, handleExited } = useDeferredDrawerClose({ onExited, open });

  const handleClose = useCallback<ScheduledTransactionEditDialogProps['onClose']>(
    (isDirty, forceClose) => {
      animateThen(() => onClose(isDirty, forceClose));
    },
    [animateThen, onClose],
  );

  const handleBack = useMemo(() => (onBack ? () => animateThen(onBack) : undefined), [animateThen, onBack]);

  return (
    <StandardDrawer
      closeTransition={rootModalDrawer.closeTransition}
      variant='overlay'
      onClose={() => handleClose()}
      onExited={handleExited}
      open={drawerOpen}
      showScrim={false}
      width={rootModalDrawer.width ?? 'Medium'}
    >
      <ScheduledTransactionEditDialog
        {...dialogProps}
        onBack={handleBack}
        onClose={handleClose}
        open
        rootModalDrawer={rootModalDrawer}
      />
    </StandardDrawer>
  );
}

export function ReviewDrawer({ edit, model, onClose, open, rootModalDrawer }: ReviewDrawerProps): React.JSX.Element {
  const { animateThen, drawerOpen, handleExited } = useDeferredDrawerClose({ open });
  const handleClose = useCallback(() => animateThen(onClose), [animateThen, onClose]);
  const handleEdit = useCallback((initialTab?: number) => animateThen(() => edit(initialTab)), [animateThen, edit]);

  return (
    <StandardDrawer
      closeTransition={rootModalDrawer.closeTransition}
      onClose={handleClose}
      onExited={handleExited}
      open={drawerOpen}
      showScrim={false}
      variant='overlay'
      width={rootModalDrawer.width ?? 'Medium'}
    >
      <ReviewDialog open model={model} onClose={handleClose} edit={handleEdit} rootModalDrawer={rootModalDrawer} />
    </StandardDrawer>
  );
}

export function ProgressDrawer({
  loadingString,
  onClose,
  open,
  rootModalDrawer,
  successString,
}: ProgressDrawerProps): React.JSX.Element {
  return (
    <StandardDrawer
      closeTransition={rootModalDrawer.closeTransition}
      onClose={onClose}
      open={open}
      showScrim={false}
      variant='overlay'
      width={rootModalDrawer.width ?? 'Medium'}
    >
      <Box
        aria-atomic='true'
        aria-label={successString ?? loadingString}
        aria-live='polite'
        role='status'
        sx={{
          alignItems: 'center',
          backgroundColor: 'background.paper',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'flex-start',
          minHeight: 350,
          overflow: 'auto',
          ...(successString && {
            pb: 3,
            pt: 2,
            px: 3,
          }),
        }}
      >
        {successString ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              py: 8,
              width: '100%',
            }}
          >
            <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <QIllustration alt='' aria-hidden name='successCheck' size='lg' />
              <Typography color='text.secondary' variant='headlineMedium' sx={{ textAlign: 'center' }}>
                {successString}
              </Typography>
            </Box>
          </Box>
        ) : (
          <LoadingView title={loadingString} size={65} style={loadingContentStyle} />
        )}
      </Box>
    </StandardDrawer>
  );
}
